import { API_BASE_PATH, API_BASE_URL, USE_MOCKS } from "../config/runtime";

export class ApiError extends Error {
  status: number;
  code: string | null;
  data: any;

  constructor(status: number, code: string | null, message: string, data: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.data = data;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}


export type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: any;
  headers?: Record<string, string>;
  token?: string | null;
  signal?: AbortSignal;
  /**
   * If false, do not attach the stored access token automatically.
   * Default: true.
   */
  auth?: boolean;
};

const TOKEN_KEY = "intimsync.access_token";

export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // ignore
  }
}

export async function request<T = any>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const base = (API_BASE_URL || "") + (API_BASE_PATH || "");
  const url = path.startsWith("http") ? path : `${base}${path}`;

  const token =
    opts.token ?? (opts.auth === false ? null : getStoredToken() ?? null);

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers || {})
  };

  const res = await fetch(url, {
    method: opts.method || (opts.body ? "POST" : "GET"),
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: "omit" // use "include" later if you move auth to cookies
  });

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
     const code =
       (data && (data.error || data.code)) ? String(data.error || data.code) : null;

     const msg =
       (data && (data.message || data.error)) ||
       res.statusText ||
       "Request failed";

      throw new ApiError(res.status, code, msg, data);
   }

  return data as T;
}

export async function maybeMock<T>(
  name: string,
  real: () => Promise<T>,
  fallback: T
): Promise<T> {
  if (USE_MOCKS) {
    return fallback;
  }
  try {
    return await real();
  } catch (err) {
    console.warn(`[mock:${name}] falling back after error`, err);
    return fallback;
  }
}
